import React, { useState, useEffect } from 'react';
import { UserIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon, ClockIcon } from '@heroicons/react/24/solid';
import { Modal, Card, CardBody, Spinner } from '../ui';

interface UserPreviewModalProps {
  userId: number;
  userName: string;
  userEmail: string;
  isOpen: boolean;
  onClose: () => void;
}

interface UserStats {
  activeTasks: number;
  completedTasks: number;
  totalTasks: number;
}

const UserPreviewModal: React.FC<UserPreviewModalProps> = ({
  userId,
  userName,
  userEmail,
  isOpen,
  onClose,
}) => {
  const [stats, setStats] = useState<UserStats>({ activeTasks: 0, completedTasks: 0, totalTasks: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      loadUserStats();
    }
  }, [isOpen, userId]);

  const loadUserStats = async () => {
    try {
      setLoading(true);
      // TODO: Replace with actual API call to get user stats
      // For now, using mock data
      setStats({
        activeTasks: 5,
        completedTasks: 12,
        totalTasks: 17,
      });
    } catch (error) {
      console.error('Error loading user stats:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="User Overview" size="md">
      {/* User Info */}
      <div className="flex items-center mb-6">
        <div className="flex-shrink-0 h-12 w-12 rounded-full bg-primary-100 flex items-center justify-center">
          <UserIcon className="h-6 w-6 text-primary-600" />
        </div>
        <div className="ml-4">
          <h3 className="text-base font-medium text-neutral-900">{userName}</h3>
          <div className="flex items-center text-sm text-neutral-500">
            <EnvelopeIcon className="h-4 w-4 mr-1" />
            {userEmail}
          </div>
        </div>
      </div>

      {/* Stats */}
      {loading ? (
        <div className="flex justify-center py-4">
          <Spinner size="md" />
        </div>
      ) : (
        <div className="space-y-3 mb-6">
          <Card variant="bordered" className="bg-primary-50">
            <CardBody className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <ClockIcon className="h-5 w-5 text-primary-600 mr-2" />
                  <span className="text-sm font-medium text-neutral-700">Active Tasks</span>
                </div>
                <span className="text-lg font-bold text-primary-600">{stats.activeTasks}</span>
              </div>
            </CardBody>
          </Card>
          <Card variant="bordered" className="bg-success-50">
            <CardBody className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <CheckCircleIcon className="h-5 w-5 text-success-600 mr-2" />
                  <span className="text-sm font-medium text-neutral-700">Completed Tasks</span>
                </div>
                <span className="text-lg font-bold text-success-600">{stats.completedTasks}</span>
              </div>
            </CardBody>
          </Card>
        </div>
      )}
    </Modal>
  );
};

export default UserPreviewModal;

